import React from 'react';
import { Box, Container, Typography, Divider, Button, Paper, Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Logout as LogoutIcon } from '@mui/icons-material';
import { getIcctPublicSiteBase } from '../../config/portalHomeContent';

const defaultHeaderImage = '/assets/images/header_banner3.png';
const venkateswaraLogo = '/assets/images/Venkateswara_logo.png';
const sivaLogo = '/assets/images/Siva_logo.png';

/**
 * App shell: org banner (logo, name, tagline, contact), optional banner image, content, footer.
 * Set showOrgHeader={false} to hide the top org card and show org lines in the footer instead.
 */
const PageLayout = ({
  children,
  headerImage = defaultHeaderImage,
  contentMaxWidth = 'md',
  showHeaderImage = true,
  showOrgHeader = true,
  orgTitle = 'India Cultural Center and Temple',
  /** Shown under org title (bold, green). */
  orgTagline = '|| Dharmo Rakshati Rakshitah ||',
  orgAddress = '12005 Highway 64, Eads, TN 38028',
  orgEmail = 'icct@icctmemphis.org',
  orgPhone = '(901) 202-6070',
  orgNonprofitLine = 'Registered 501(c)(3) nonprofit organization',
  orgTaxId = '58-1522313',
  privacyNote = 'Your information is protected and used strictly for ICCT internal purposes only.',
  portalTitle = '',
  logoHeight = 64,
  contentPadding = { xs: 1, sm: 2 },
  /** When true, omit the top-right “Signed in as” / Log out strip (portal renders its own). */
  hideSignedInBar = false,
}) => {
  const navigate = useNavigate();
  const { staff, logout } = useAuth();
  const icctParentSiteUrl = getIcctPublicSiteBase();
  const footerYear = new Date().getFullYear();

  const orgContactRightBlock = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: { xs: 'center', sm: 'flex-end' },
        gap: 1.5,
        textAlign: { xs: 'center', sm: 'right' },
        flexShrink: 0,
        minWidth: 0,
        maxWidth: { sm: '55%' },
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          {orgAddress}
        </Typography>
        <Typography component="div" variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.5 }}>
          Phone: {orgPhone}
          {' · '}
          Email:{' '}
          <Box
            component="a"
            href={`mailto:${orgEmail}`}
            sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            {orgEmail}
          </Box>
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.5 }}>
          {orgNonprofitLine} · Tax ID: {orgTaxId}
        </Typography>
      </Box>
      <Box
        aria-label="Temple logos"
        sx={{
          position: 'relative',
          width: { xs: logoHeight + 16, sm: logoHeight + 24 },
          height: { xs: logoHeight + 16, sm: logoHeight + 24 },
          maxHeight: { xs: 100, sm: 112 },
          flex: '0 0 auto',
          borderRadius: '50%',
          overflow: 'hidden',
          '@keyframes logoCycle1': {
            '0%, 46%': { opacity: 1 },
            '54%, 100%': { opacity: 0 },
          },
          '@keyframes logoCycle2': {
            '0%, 46%': { opacity: 0 },
            '54%, 100%': { opacity: 1 },
          },
        }}
      >
        <Box
          component="img"
          src={venkateswaraLogo}
          alt="Venkateswara"
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: '50%',
            filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.18))',
            animation: 'logoCycle1 14s ease-in-out infinite',
          }}
        />
        <Box
          component="img"
          src={sivaLogo}
          alt="Siva"
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: '50%',
            filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.18))',
            opacity: 0,
            animation: 'logoCycle2 14s ease-in-out infinite',
          }}
        />
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '100vh',
        pt: '20px',
        pb: { xs: 2, md: 4 },
        background:
          'radial-gradient(1200px 420px at 50% -180px, rgba(255, 248, 209, 0.55) 0%, rgba(255, 248, 209, 0) 70%), linear-gradient(180deg, #FFFDF3 0%, #FFF9E6 45%, #FFFDF5 100%)',
      }}
    >
      {showOrgHeader && (
        <Box sx={{ width: '95%', maxWidth: '95%', mx: 'auto', mb: 2 }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 2.5 },
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'rgba(255, 183, 120, 0.5)',
              bgcolor: '#FFEAD4',
              background:
                'linear-gradient(180deg, rgba(255, 243, 228, 0.98) 0%, rgba(255, 220, 185, 0.9) 100%)',
              boxShadow: '0 10px 26px rgba(210, 120, 40, 0.12)',
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 2.5, sm: 3 }}
              alignItems={{ xs: 'center', sm: 'center' }}
              justifyContent="space-between"
            >
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                sx={{ flex: 1, minWidth: 0, alignSelf: { xs: 'center', sm: 'flex-start' } }}
              >
                <Box
                  component="a"
                  href={icctParentSiteUrl}
                  aria-label="India Cultural Center and Temple — go to icctmemphis.org"
                  sx={{
                    display: 'inline-flex',
                    flexShrink: 0,
                    lineHeight: 0,
                    textDecoration: 'none',
                    borderRadius: 1,
                    '&:hover': { opacity: 0.88 },
                  }}
                >
                  <Box
                    component="img"
                    src="/assets/images/ICCT-Logo-trans.webp"
                    alt="ICCT Logo"
                    sx={{
                      height: { xs: logoHeight + 16, sm: logoHeight + 24 },
                      width: 'auto',
                      maxHeight: { xs: 100, sm: 112 },
                      objectFit: 'contain',
                    }}
                  />
                </Box>
                <Box sx={{ minWidth: 0, textAlign: 'center' }}>
                  <Typography
                    component="h1"
                    sx={{
                      fontWeight: 800,
                      fontSize: { xs: '1.15rem', sm: '1.35rem', md: '1.5rem' },
                      lineHeight: 1.25,
                      letterSpacing: 0.15,
                      color: 'text.primary',
                    }}
                  >
                    {orgTitle}
                  </Typography>
                  {orgTagline ? (
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 0.75,
                        fontWeight: 700,
                        lineHeight: 1.45,
                        color: 'success.dark',
                        letterSpacing: 0.02,
                      }}
                    >
                      {orgTagline}
                    </Typography>
                  ) : null}
                </Box>
              </Stack>
              {orgContactRightBlock}
            </Stack>
          </Paper>
          {portalTitle ? (
            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                letterSpacing: 0.4,
                textAlign: 'center',
                mt: 1.5,
                color: 'text.primary',
              }}
            >
              {portalTitle}
            </Typography>
          ) : null}
        </Box>
      )}
      <Box
        sx={{
          width: '95%',
          maxWidth: '95%',
          mx: 'auto',
          px: { xs: 2, sm: 4 },
          pt: 0,
          pb: { xs: 1, sm: 2 },
          background:
            'linear-gradient(180deg, rgba(255, 255, 255, 0.97) 0%, rgba(255, 252, 242, 0.96) 52%, rgba(255, 248, 225, 0.9) 100%)',
          border: '1px solid',
          borderColor: 'rgba(227, 207, 142, 0.5)',
          borderRadius: 3,
          boxShadow: '0 14px 34px rgba(161, 122, 38, 0.12)',
        }}
      >
        {showHeaderImage && (
          <Box
            component="img"
            src={headerImage}
            alt=""
            sx={{
              width: '100%',
              maxHeight: '300px',
              objectFit: 'cover',
              display: 'block',
              marginBottom: 2,
              mx: 'auto',
              borderRadius: 3,
            }}
          />
        )}

        {staff && !hideSignedInBar ? (
          <Box sx={{ pt: '5px', mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              Signed in as <strong>{staff?.name || staff?.email || 'User'}</strong>
            </Typography>
            <Button
              variant="contained"
              color="error"
              size="small"
              startIcon={<LogoutIcon />}
              onClick={async () => {
                await logout();
                navigate('/login');
              }}
            >
              Log out
            </Button>
          </Box>
        ) : null}

        <Container maxWidth={contentMaxWidth === false ? false : contentMaxWidth} sx={{ px: { xs: 2, sm: 4 }, py: contentPadding, width: '100%' }}>
          {children}
        </Container>

        <Divider sx={{ mt: 3 }} />
        <Box sx={{ textAlign: 'center', mt: 2, pb: 2, px: 1 }}>
          {!showOrgHeader && (
            <>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{orgTitle}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{orgAddress}</Typography>
              <Typography component="div" variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                Phone: {orgPhone}
                {' · '}
                Email:{' '}
                <Box
                  component="a"
                  href={`mailto:${orgEmail}`}
                  sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                >
                  {orgEmail}
                </Box>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {orgNonprofitLine} · Tax ID: {orgTaxId}
              </Typography>
            </>
          )}
          <Typography
            variant="caption"
            display="block"
            color="text.secondary"
            sx={{ mt: showOrgHeader ? 0 : 1.25, maxWidth: 520, mx: 'auto', lineHeight: 1.5 }}
          >
            {privacyNote}
          </Typography>
          <Typography
            variant="caption"
            display="block"
            color="text.secondary"
            sx={{ mt: 0.5, fontWeight: 700 }}
          >
            {footerYear} {orgTitle}
          </Typography>
          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.75 }}>
            <Box
              component="a"
              href="https://icctmemphis.org/information/privacy-policy/"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              Privacy Policy
            </Box>
            {' | '}
            <Box
              component="a"
              href="https://icctmemphis.org/information/refund-policy/"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              Refund Policy
            </Box>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default PageLayout;
